'use client'
// Carte tutoriel d'onboarding (4 étapes). Extrait de app/page.tsx.

export function TutorialCard({ onDismiss }: { onDismiss: () => void }) {

  // Premiers pas (1er oct. 2026) : le travail d'abord — projet, échanges, poste, assistant.
  const steps = [
    {
      icon: '◆',
      label: 'Créez un projet',
      desc: 'Donnez-lui un but, un cadrage et des objectifs : il devient le contexte de vos documents et échanges.',
      href: '/bureau/affaires?tab=projects',
      tag: 'PROJETS',
    },
    {
      icon: '▤',
      label: 'Travaillez en équipe',
      desc: 'Messages directs, groupes et canaux d’équipe de votre organisation — l’historique reste quand quelqu’un part.',
      href: '/messages',
      tag: 'MESSAGES',
    },
    {
      icon: '⬡',
      label: 'Connectez un poste',
      desc: 'Installez l’agent : le dossier synchronisé rejoint votre Bureau. Chaque capacité est approuvée par vous.',
      href: '/parametres?tab=agents',
      tag: 'AGENTS',
    },
    {
      icon: '🦋',
      label: 'Demandez à Ti-Coune',
      desc: 'Recherche, règlements, marchés, documents : l’assistant interroge les modules et cite ses sources quand il en a.',
      href: '/messages',
      tag: 'ASSISTANT',
    },
  ]

  return (
    <div style={{
      marginBottom: '32px',
      padding: '24px',
      background: `
        linear-gradient(
          180deg,
          rgba(79,124,255,0.08),
          rgba(79,124,255,0.03)
        )
      `,
      border: '1px solid rgba(79,124,255,0.22)',
      borderRadius: 16,
      position: 'relative',
      overflow: 'hidden',
      backdropFilter: 'blur(12px)',
      animation: 'fadeUp 0.45s ease-out',
    }}>

      {/* Glow */}
      <div style={{
        position: 'absolute',
        top: '-80px',
        right: '-80px',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'rgba(79,124,255,0.12)',
        filter: 'blur(80px)',
        pointerEvents: 'none',
      }} />

      {/* Close */}
      <button
        onClick={onDismiss}
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          width: 28,
          height: 28,
          borderRadius: 8,
          border: '1px solid var(--border-dim)',
          background: 'rgba(255,255,255,0.02)',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          fontSize: 14,
        }}
      >
        ×
      </button>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 18,
      }}>
        <div style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: 'rgba(79,124,255,0.15)',
          border: '1px solid rgba(79,124,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--accent)',
          fontSize: 14,
        }}>
          ◈
        </div>

        <div>
          <div style={{
            fontSize: 10,
            color: 'var(--accent)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.14em',
            marginBottom: 2,
          }}>
            AMIRAL BLANC
          </div>

          <div className="font-display" style={{
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--white)',
          }}>
            Premiers pas
          </div>
        </div>
      </div>

      {/* Description */}
      <div style={{
        fontSize: 12,
        color: 'var(--text-muted)',
        lineHeight: 1.6,
        marginBottom: 20,
        fontFamily: 'var(--font-mono)',
      }}>
        Projets, CRM, messages, documents et agents de poste dans un même environnement —
        l’agent propose, vous validez, le système exécute.
      </div>

      {/* Steps */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
        gap: 12,
      }}>
        {steps.map((s, i) => (
          <a
            key={s.label}
            href={s.href}
            style={{
              display: 'block',
              padding: '16px',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid var(--border-dim)',
              borderRadius: 14,
              textDecoration: 'none',
              color: 'inherit',
              transition: 'all 0.15s ease',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget
              el.style.transform = 'translateY(-2px)'
              el.style.borderColor = 'rgba(79,124,255,0.4)'
              el.style.background = 'rgba(79,124,255,0.04)'
            }}
            onMouseLeave={e => {
              const el = e.currentTarget
              el.style.transform = 'none'
              el.style.borderColor = 'var(--border-dim)'
              el.style.background = 'rgba(255,255,255,0.02)'
            }}
          >

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <div style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: 'rgba(79,124,255,0.15)',
                border: '1px solid rgba(79,124,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--accent)',
                fontSize: 11,
                fontWeight: 700,
              }}>
                {i + 1}
              </div>

              <div style={{
                fontSize: 9,
                padding: '3px 6px',
                borderRadius: 999,
                background: 'rgba(79,124,255,0.1)',
                border: '1px solid rgba(79,124,255,0.2)',
                color: 'var(--accent)',
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.08em',
              }}>
                {s.tag}
              </div>
            </div>

            <div style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--white)',
              marginBottom: 6,
            }}>
              {s.label}
            </div>

            <div style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1.5,
            }}>
              {s.desc}
            </div>
          </a>
        ))}
      </div>
    </div>
  )
}
